import type { ReactNode } from "react";
import { StepShell, StepHeading } from "@codel/dova-ds";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--interactive)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 mt-0.5"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const POINTS: { icon: ReactNode; title: string; body: string }[] = [
  {
    icon: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
    title: "Your messages are encrypted",
    body: "In transit and at rest, on a private network that's never exposed to the public internet.",
  },
  {
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="4.9" y1="4.9" x2="19.1" y2="19.1" />
      </>
    ),
    title: "Your data is never used to train AI",
    body: "Dova holds zero-retention agreements with its language-model providers.",
  },
  {
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    title: "Enterprise-level protection",
    body: "Built on HIPAA-compliant infrastructure.",
  },
  {
    icon: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    title: "Your therapist sees themes, not messages",
    body: "They see the topics, patterns, and suggested next steps from your conversations, never the raw messages. The one exception is a safety alert (signs of abuse, self-harm, or risk to a child), where Dova shares what's needed to keep you safe.",
  },
];

export function S7bPrivacy({
  onNext,
  onBack,
  currentStepIndex,
  totalSteps,
}: StepProps) {
  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      footer={<Button onClick={onNext}>Continue</Button>}
    >
      <StepHeading title="Built for private connection" />

      <div className="flex flex-col gap-6">
        {POINTS.map((p) => (
          <div key={p.title} className="flex gap-4">
            <Icon>{p.icon}</Icon>
            <div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">
                {p.title}
              </h3>
              <p className="text-base leading-[1.5] text-[var(--text-secondary)]">
                {p.body}
              </p>
            </div>
          </div>
        ))}
      </div>
    </StepShell>
  );
}
